import { Card, BlockStack, InlineStack, RadioButton } from '@shopify/polaris';
import { LabelPrintMethodSelect } from '#client/components/features/LabelPrinting/components/LabelPrintMethodSelect';
import { ZebraPrinterPicker } from '#client/components/features/LabelPrinting/components/ZebraPrinterPicker';
import { LabelCatalogSelect } from '#client/components/features/LabelPrinting/components/LabelCatalogSelect';

/**
 * Shared print settings: print method, Zebra location/printer, price catalog, and SKU
 * type. Rendered by both tabs so the choices survive a tab switch.
 *
 * `showStoreSelect` controls the Store (location) dropdown only, not the printer dropdown.
 * By SKU / UPC has no other use for a location control, so it passes `showStoreSelect={true}`
 * and gets both Location and Printer here. By Style's search card already has a Store select
 * that scopes its inventory lookup, so it passes `showStoreSelect={false}` to keep exactly one
 * Store control on screen — this card still shows the Printer dropdown there, filtered to
 * whichever store is selected, so Zebra printing stays reachable from that tab too.
 */
export default function PrintSettingsCard({
    printMode,
    onPrintModeChange,
    skuType,
    onSkuTypeChange,
    catalogs,
    priceSelection,
    onPriceSelectionChange,
    zebraSelection,
    labelPrintersQuery,
    enableZebraCloudPrinting,
    showStoreSelect,
}) {
    const showPrinterForm = printMode === 'zebra';

    return (
        <Card>
            <BlockStack gap="400">
                <LabelPrintMethodSelect value={printMode} onChange={onPrintModeChange} enableZebraOption={enableZebraCloudPrinting} />

                {/* Location (when shown) decides which Market catalog price prints on the
                    label. Printer is independent of that and only needed for Zebra printing. */}
                <ZebraPrinterPicker
                    isLoading={labelPrintersQuery.isLoading}
                    locationOptions={zebraSelection.locationOptions}
                    printerOptions={zebraSelection.printerOptions}
                    selectedLocationGid={zebraSelection.selectedLocationGid}
                    onLocationChange={zebraSelection.setSelectedLocationGid}
                    selectedPrinterDeviceId={zebraSelection.selectedPrinterDeviceId}
                    onPrinterChange={zebraSelection.setSelectedPrinterDeviceId}
                    printerDisabled={!zebraSelection.selectedLocationGid || zebraSelection.availablePrinters.length === 0}
                    printerHelpText={zebraSelection.printerHelpText}
                    showPrinter={showPrinterForm}
                    showLocation={showStoreSelect}
                    locationHelpText="Sets the Zebra printer, and which price catalogs apply."
                />

                <LabelCatalogSelect catalogs={catalogs} locationGid={zebraSelection.selectedLocationGid} value={priceSelection} onChange={onPriceSelectionChange} />

                <InlineStack gap="200">
                    <RadioButton label="Work World SKU" checked={skuType === 'sku'} id="sku" name="skuType" onChange={() => onSkuTypeChange('sku')} />
                    <RadioButton label="Amazon FNSKU" checked={skuType === 'fnsku'} id="fnsku" name="skuType" onChange={() => onSkuTypeChange('fnsku')} />
                </InlineStack>
            </BlockStack>
        </Card>
    );
}
